import Image from 'next/image';
import dynamic from 'next/dynamic';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { TwitterShareButton } from 'react-share';
import { useUserProvider } from '@/context/UserProvider';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { TFunction, useTranslation } from 'next-i18next';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { getRewardAvailableApi } from '@/lib/service/home';
import { NextRouter, useRouter } from 'next/router';
import { cn } from '@/lib/utils';

const FreeYoutobeModal = dynamic(() => import('./FreeYoutobeModal'), { ssr: false });

export interface RewardAvailable {
  reward_type: string;
  description: string;
  reward_days: number;
  available: boolean;
  remaining_count: number;
  next_available_time: string;
}

type IProps = {
  tabFunc?: 'story' | 'highlights' | 'post';
  fetchTimesRequest: () => void;
};

function getHeaderDatae(t: TFunction, tabFunc: IProps['tabFunc']) {
  if (tabFunc === 'post') {
    return {
      title: t('🎁 View & Save Instagram Posts for Free'),
      desc: t('Get full IG Post access in seconds — totally free!'),
    };
  }

  if (tabFunc === 'highlights') {
    return {
      title: t('🎁 View & Save Instagram Highlights for Free'),
      desc: t('Get full IG Highlights access in seconds — totally free!'),
    };
  }

  return {
    title: t('🎁 View & Save Instagram Stories for Free'),
    desc: t('Get full IG Story access in seconds — totally free!'),
  };
}

const list = ['story', 'highlights', 'post'];

function getTwitterData(router: NextRouter, uid: MutableRefObject<string>, tabFunc?: string) {
  if (
    list.includes(tabFunc) &&
    (router.pathname === '/web-viewer-for-instagram' ||
      (router.pathname === '/download-from-instagram/[id]' && router.query?.id === 'post'))
  ) {
    return {
      url: `https://www.dolphinradar.com/web-viewer-for-instagram?share=${uid.current}`,
      title: `Finally watched Instagram Posts without tipping anyone off—no views, no receipts. 👻✨ Game-changer from @DolphinRadar! Try it →`,
      hashtags: ['IGpostviewer', 'PostViewer'],
    };
  }

  if (
    list.includes(tabFunc) &&
    (router.pathname === '/instagram-viewer/[id]' || router.pathname === '/download-from-instagram/[id]') &&
    router.query?.id === 'highlights'
  ) {
    return {
      url: `https://www.dolphinradar.com/instagram-viewer/highlights?share=${uid.current}`,
      title: `Finally watched Instagram Posts without tipping anyone off—no views, no receipts. 👻✨ Game-changer from @DolphinRadar! Try it →`,
      hashtags: ['IGpostviewer', 'HighlightsViewer'],
    };
  }

  return {
    url: `https://www.dolphinradar.com/anonymous-instagram-story-viewer?share=${uid.current}`,
    title: `Finally watched Instagram Stories without tipping anyone off—no views, no receipts. 👻✨Game-changer from @DolphinRadar! Try it →`,
    hashtags: ['IGstoryviewer', 'StoryViewer'],
  };
}

export default function InstagramStoresFree2({ fetchTimesRequest, tabFunc }: IProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { isLogin, onOpenLoginModal } = useUserProvider();
  const uid = useRef<string>('');
  const { t } = useTranslation('common');
  const [staList, setStaList] = useState<number[]>([1, 1, 1]);

  const router = useRouter();

  const data = [
    {
      title: 'Watch a 30s Video',
      tips: 'Free All Day',
      descs: ['Just 30 seconds to unlock unlimited access for the day.', 'Do it once a day and come back anytime.'],
      icon: '/images/freetools/youtobe-icon.webp',
      btnText: 'Watch Now',
      rb: `${staList[0]}/1 day`,
    },
    {
      title: 'Tag Us on X',
      tips: 'Get 15 Days Free',
      descs: [
        'Tag @DolphinRadar in a tweet to unlock 2 weeks of full access, including tracking for 1 Instagram account.',
        'The reward may take a bit to show up—thanks for your patience. Limit one reward per user.',
      ],
      icon: '/images/freetools/x-icon.webp',
      btnText: 'Try on X',
      rb: `${staList[1]}/1`,
    },
    {
      title: 'Share on Reddit',
      tips: 'Get 30 Days Free',
      descs: [
        'Make a post about us (we’ll review it) — unlock a whole month free. One reward per account each month.',
        '👉 Submit your screenshot here after posting.',
      ],
      icon: '/images/freetools/reddit-icon.webp',
      btnText: 'Share Now',
      rb: `${staList[2]}/1`,
    },
  ];

  useEffect(() => {
    if (!isLogin) return;
    const auth = getAuth();
    getRewardAvailable();
    onAuthStateChanged(auth, (user) => {
      if (user) {
        uid.current = Buffer.from(user.uid, 'utf-8').toString('base64');
      }
    });
  }, [isLogin]);

  const headerData = getHeaderDatae(t, tabFunc);
  const twitterData = getTwitterData(router, uid, tabFunc);

  const getRewardAvailable = async () => {
    const res = await getRewardAvailableApi();
    if (res.code === 0) {
      setStaList(res.data.available_rewards.map((item: RewardAvailable) => item.remaining_count));
    }
  };

  return (
    <div
      className={cn(
        "mt-32 bg-[url('https://ins-interact-new.s3.us-west-2.amazonaws.com/ins_image/store-view-free-tools-mobile.webp')]",
        "lg:bg-[url('https://ins-interact-new.s3.us-west-2.amazonaws.com/ins_image/store-view-free-tools.webp')]",
        'bg-cover border  bg-bottom lg:bg-center bg-no-repeat py-10 lg:py-14 px-2 lg:px-10 rounded-lg scroll-mt-32'
      )}
      id="free-tools-title"
    >
      <h2 className="text-center text-2xl font-bold mb-4">{headerData.title}</h2>
      <p className="text-center text-base text-gray-500 lg:mb-10 mb-6">{headerData.desc}</p>
      <ul className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data.map((item, idx) => (
          <li key={item.title} className="border bg-white rounded-lg p-5 relative hover:border-[#725cff]">
            <h3 className="text-base font-bold mb-2">{t(item.title)}</h3>
            <div className="lg:mb-10 mb-16">
              {item.descs.map((p) => (
                <p key={p} className="text-sm text-gray-500 mb-2" dangerouslySetInnerHTML={{ __html: t(p) }} />
              ))}
            </div>
            <div className="absolute  bottom-4 right-0 left-0 flex justify-between items-center px-4">
              {idx == 0 && (
                <div
                  className="flex items-center text-sm text-gray-500 hover:bg-[#725cff] group border rounded-sm  gap-2 px-2 py-1  cursor-pointer"
                  onClick={() => {
                    if (isLogin) {
                      ga_logEvent(EventName.reward_watch_video_click);
                      setIsOpen(true);
                    } else {
                      onOpenLoginModal(true);
                    }
                  }}
                >
                  <Image src={item.icon} alt={item.title} width={20} height={20} />
                  <span className="text-sm font-medium text-black group-hover:text-white">{t(item.btnText)}</span>
                </div>
              )}
              {idx == 2 && (
                <div
                  className="flex items-center text-sm text-gray-500 border rounded-sm hover:bg-[#725cff] group   gap-2 px-2 py-1 cursor-pointer"
                  onClick={() => {
                    ga_logEvent(EventName.reward_reddit_click);
                    window.open('https://forms.gle/j1hgFesbjUyBZvut5', '_blank');
                  }}
                >
                  <Image src={item.icon} alt={item.title} width={20} height={20} />
                  <span className="text-sm font-medium text-black group-hover:text-white">{t(item.btnText)}</span>
                </div>
              )}
              {idx == 1 &&
                (isLogin ? (
                  <TwitterShareButton
                    onClick={() => {
                      ga_logEvent(EventName.reward_x_click);
                    }}
                    url={twitterData.url}
                    title={twitterData.title}
                    hashtags={twitterData.hashtags}
                  >
                    <div className="flex items-center text-sm text-gray-500 border rounded-sm  gap-2 px-2 py-1 cursor-pointer hover:bg-[#725cff] group ">
                      <Image src={item.icon} alt={item.title} width={20} height={20} />
                      <span className="text-sm font-medium text-black group-hover:text-white">{t(item.btnText)}</span>
                    </div>
                  </TwitterShareButton>
                ) : (
                  <div
                    className="flex items-center text-sm text-gray-500 border rounded-sm  gap-2 px-2 py-1 hover:bg-[#725cff] group cursor-pointer"
                    onClick={() => {
                      onOpenLoginModal(true);
                    }}
                  >
                    <Image src={item.icon} alt={item.title} width={20} height={20} />
                    <span className="text-sm font-medium text-black group-hover:text-white">{t(item.btnText)}</span>
                  </div>
                ))}

              <div className="text-sm text-gray-500">{item.rb}</div>
            </div>
            <div className="absolute top-2  right-2 bg-[#fde0c8] py-1 px-2 text-sm font-bold flex items-center pt-1 text-[#f57200] rounded-full">
              {t(item.tips)}
              <span className="text-[10px] ml-1">🎁</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="text-center text-base text-black mt-10 lg:w-full w-[80%] mx-auto lg:mb-0 mb-16">
        <div dangerouslySetInnerHTML={{ __html: t('🔥 1000 users already got free access today!') }}></div>
      </div>
      <FreeYoutobeModal
        isOpen={isOpen}
        uid={uid.current}
        getRewardAvailable={async () => {
          getRewardAvailable();
          fetchTimesRequest();
        }}
        onClose={() => setIsOpen(false)}
      />
    </div>
  );
}
